import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { House, MapPin, Ruler, Sun, CloudSun, Moon } from '@phosphor-icons/react';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import axios from 'axios';
import { API_URL } from '../config/api';

export const SharedPropertyPage = () => {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSharedProperty();
  }, [shareToken]);

  const fetchSharedProperty = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/property/shared/${shareToken}`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Property not found or link expired');
    } finally {
      setLoading(false);
    }
  };

  const getSunIcon = (exposure) => {
    switch (exposure) {
      case 'Full Sun': return <Sun weight="duotone" size={16} className="text-amber-500" />;
      case 'Partial Shade': return <CloudSun weight="duotone" size={16} className="text-blue-500" />;
      case 'Full Shade': return <Moon weight="duotone" size={16} className="text-indigo-500" />;
      default: return <Sun weight="duotone" size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-green-950/20 dark:to-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading property data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-green-950/20 dark:to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <House weight="duotone" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium text-destructive">{error}</p>
            <p className="text-sm text-muted-foreground mt-2">
              This link may have expired or the property is no longer shared.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { property, zones, owner, total_sqft } = data;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-green-950/20 dark:to-background">
      {/* Header */}
      <header className="bg-white/80 dark:bg-background/80 backdrop-blur-xl border-b border-border shadow-sm sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-center gap-3">
          <TurfPulseLogoSVG animated={false} size={32} />
          <span className="text-lg font-bold text-primary">TurfPulse</span>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto p-4 pb-12">
        {/* Property Card */}
        <Card className="mb-4 border-2 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <House weight="duotone" size={24} className="text-primary" />
              {property.name || 'Property'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground flex items-center gap-1 mb-4">
              <MapPin weight="fill" size={14} className="text-red-500" />
              {[property.address, property.city, property.state, property.zip].filter(Boolean).join(', ') || 'Location not specified'}
            </p>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Owner</p>
                <p className="font-medium">{owner.name}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Grass Type</p>
                <p className="font-medium capitalize">{owner.grass_type?.replace('-', ' ') || 'Not specified'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Square Footage */}
        <Card className="mb-4 bg-primary/5 border-2 border-primary/30">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Lawn Area</p>
                <p className="text-4xl font-bold text-primary">{total_sqft.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">square feet</p>
              </div>
              <Ruler weight="duotone" size={56} className="text-primary/30" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {zones.length} zone{zones.length !== 1 ? 's' : ''} measured
            </p>
          </CardContent>
        </Card>

        {/* Zone List */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Zone Measurements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {zones.map((zone, idx) => (
                <div key={zone.id || idx} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="font-medium">{zone.name || `Zone ${idx + 1}`}</p>
                    {zone.sun_exposure && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        {getSunIcon(zone.sun_exposure)}
                        {zone.sun_exposure}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-primary">{(zone.square_footage || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">sq ft</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-muted-foreground">
            Shared via TurfPulse • getturfpulse.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedPropertyPage;
